import { useCallback, useEffect, useState } from 'react'

/**
 * Per-view column selection. Order of the array is the column order,
 * so adding and removing fields is also how the LO reorders them.
 */
export function useVisibleFields(storageKey: string, defaults: string[]) {
  const [fieldIds, setFieldIds] = useState<string[]>(() => {
    try {
      const stored = window.localStorage.getItem(storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) return parsed as string[]
      }
    } catch {
      // ignore unreadable storage and fall back to defaults
    }
    return defaults
  })

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(fieldIds))
    } catch {
      // storage is best-effort only
    }
  }, [storageKey, fieldIds])

  const toggle = useCallback((id: string) => {
    setFieldIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    )
  }, [])

  const remove = useCallback((id: string) => {
    setFieldIds((prev) => prev.filter((item) => item !== id))
  }, [])

  const move = useCallback((id: string, direction: -1 | 1) => {
    setFieldIds((prev) => {
      const index = prev.indexOf(id)
      const target = index + direction
      if (index === -1 || target < 0 || target >= prev.length) return prev
      const next = [...prev]
      next[index] = next[target]
      next[target] = id
      return next
    })
  }, [])

  const reset = useCallback(() => setFieldIds(defaults), [defaults])

  return { fieldIds, toggle, remove, move, reset }
}
